import { TILE } from './constants';
import { T_ONEWAY, T_SOLID, tileAt, type Room } from './room';

/**
 * Box-against-tile collision for one lane's grid. Speeds stay below one tile per tick, so a body
 * can't pass through a tile in one step.
 */

/** Keeps edges that touch a tile boundary from counting as inside the next tile. */
export const EPS = 0.001;

/** An axis-aligned box in world pixels; x, y is the top-left corner. */
export interface Body {
  x: number;
  y: number;
  w: number;
  h: number;
  vx: number;
  vy: number;
}

/** Moves a body along x and stops it at solid tiles. Returns true when it hit a wall. */
export function moveBodyX(room: Room, lane: number, b: Body, dx: number): boolean {
  if (dx === 0) return false;
  b.x += dx;
  const top = Math.floor(b.y / TILE);
  const bottom = Math.floor((b.y + b.h - EPS) / TILE);
  if (dx > 0) {
    const right = Math.floor((b.x + b.w - EPS) / TILE);
    for (let ty = top; ty <= bottom; ty++) {
      if (tileAt(room, lane, right, ty) === T_SOLID) {
        b.x = right * TILE - b.w;
        return true;
      }
    }
  } else {
    const left = Math.floor(b.x / TILE);
    for (let ty = top; ty <= bottom; ty++) {
      if (tileAt(room, lane, left, ty) === T_SOLID) {
        b.x = (left + 1) * TILE;
        return true;
      }
    }
  }
  return false;
}

/**
 * Moves a body along y. Solid tiles stop it both ways; one-way platforms only stop a body that
 * was above them before the move, and never stop flyers (`oneWay` false).
 */
export function moveBodyY(
  room: Room,
  lane: number,
  b: Body,
  dy: number,
  oneWay = true,
): 'none' | 'floor' | 'ceiling' {
  if (dy === 0) return 'none';
  const prevBottom = b.y + b.h;
  b.y += dy;
  const left = Math.floor(b.x / TILE);
  const right = Math.floor((b.x + b.w - EPS) / TILE);
  if (dy > 0) {
    const row = Math.floor((b.y + b.h - EPS) / TILE);
    for (let tx = left; tx <= right; tx++) {
      const t = tileAt(room, lane, tx, row);
      if (t === T_SOLID || (oneWay && t === T_ONEWAY && prevBottom <= row * TILE + EPS)) {
        b.y = row * TILE - b.h;
        return 'floor';
      }
    }
  } else {
    const row = Math.floor(b.y / TILE);
    for (let tx = left; tx <= right; tx++) {
      if (tileAt(room, lane, tx, row) === T_SOLID) {
        b.y = (row + 1) * TILE;
        return 'ceiling';
      }
    }
  }
  return 'none';
}

/** True when the box would sit inside a solid tile in that lane (a lane switch would trap it). */
export function overlapsSolid(room: Room, lane: number, b: Body): boolean {
  const left = Math.floor(b.x / TILE);
  const right = Math.floor((b.x + b.w - EPS) / TILE);
  const top = Math.floor(b.y / TILE);
  const bottom = Math.floor((b.y + b.h - EPS) / TILE);
  for (let ty = top; ty <= bottom; ty++) {
    for (let tx = left; tx <= right; tx++) {
      if (tileAt(room, lane, tx, ty) === T_SOLID) return true;
    }
  }
  return false;
}
